import {IconButton, openDeleteDialog} from 'platform/components';
import {Box, HStack} from 'platform/foundation';

import {useCallback, useMemo, useState} from 'react';

import {isNil, isNotNil} from 'ramda';

import {suffixTestId, TestIdProps} from 'shared/web';

import i18n from '../../../../i18n/i18n';
import {
  useCreateRelativeFilterMutation,
  useDeleteRelativeFilterMutation,
  useUpdateRelativeFilterMutation,
} from '../../../../store/omneticApi';
import {RelativeFilterConfigBody, RelativeFilterResponseBody} from '../../../../types/api/api';
import {handleApiError} from '../../../../utils/handleApiError';
import {PresetEditorDialog} from './components/PresetEditorDialog';
import {SavedFiltersDropdown} from './components/SavedFiltersDropdown';
import {RelativeFilterPresetsController} from './hooks/useRelativeFilterPresetsController';

interface SavedFiltersProps extends TestIdProps {
  presetController: RelativeFilterPresetsController;
}

type DialogState = {mode: 'closed'} | {mode: 'create'} | {mode: 'edit'; presetId: string};

export function SavedFilters(props: SavedFiltersProps) {
  const {presetController} = props;

  const [createRelativeFilter] = useCreateRelativeFilterMutation();
  const [updateRelativeFilter] = useUpdateRelativeFilterMutation();
  const [deleteRelativeFilter] = useDeleteRelativeFilterMutation();
  const [dialogState, setDialogState] = useState<DialogState>({mode: 'closed'});

  const handlePresetChange = useCallback(
    (presetId: string | null) => {
      if (isNil(presetId) || presetController.isCatalogueLoading) {
        return;
      }
      const found = presetController.presets.find((preset) => preset.id === presetId);
      if (isNil(found)) {
        return;
      }
      presetController.setActivePresetId(presetId);
      presetController.applyPreset(found.config);
    },
    [presetController]
  );

  const handleCreateOption = useCallback(() => {
    setDialogState({mode: 'create'});
  }, []);

  const handleSave = useCallback(
    (name: string, config: RelativeFilterConfigBody, isDefault: boolean) => {
      if (isNil(presetController.customerId)) {
        return;
      }

      if (dialogState.mode === 'create') {
        createRelativeFilter({
          customerId: presetController.customerId,
          name,
          config,
          default: isDefault,
        })
          .unwrap()
          .then(({id}) => {
            presetController.setActivePresetId(id);
            presetController.applyPreset(config);
            setDialogState({mode: 'closed'});
          })
          .catch(handleApiError);
      } else if (dialogState.mode === 'edit') {
        const editedPresetId = dialogState.presetId;
        const editedPreset = presetController.presets.find(
          (preset) => preset.id === editedPresetId
        );
        const shouldApplyPresetAfterSave = presetController.activePresetId === editedPresetId;

        if (editedPreset?.system) {
          updateRelativeFilter({
            customerId: presetController.customerId,
            id: editedPresetId,
            default: isDefault,
          })
            .unwrap()
            .then(() => setDialogState({mode: 'closed'}))
            .catch(handleApiError);
        } else {
          updateRelativeFilter({
            customerId: presetController.customerId,
            id: editedPresetId,
            name,
            config,
            default: isDefault,
          })
            .unwrap()
            .then(() => {
              if (shouldApplyPresetAfterSave) {
                presetController.applyPreset(config);
              }
              setDialogState({mode: 'closed'});
            })
            .catch(handleApiError);
        }
      }
    },
    [dialogState, presetController, createRelativeFilter, updateRelativeFilter]
  );

  const deletePreset = useCallback(
    (presetId: string) => {
      if (isNil(presetController.customerId)) {
        return Promise.resolve();
      }
      return deleteRelativeFilter({id: presetId, customerId: presetController.customerId})
        .unwrap()
        .then(() => {
          presetController.setActivePresetId((current) => (current === presetId ? null : current));
          setDialogState({mode: 'closed'});
        })
        .catch(handleApiError);
    },
    [presetController, deleteRelativeFilter]
  );

  const handleDeletePresetPress = useCallback(() => {
    if (dialogState.mode !== 'edit' || isNil(presetController.customerId)) {
      return;
    }
    const presetId = dialogState.presetId;
    const presetName =
      presetController.presets.find((preset) => preset.id === presetId)?.name ?? '';
    openDeleteDialog({
      id: 'price-report-delete-saved-filter-preset',
      text: i18n.t('entity.filterForm.savedFilters.dialog.deleteConfirm', {name: presetName}),
      onConfirm: () => deletePreset(presetId),
      'data-testid': suffixTestId('deletePresetDeleteDialog', props),
    });
  }, [dialogState, presetController, deletePreset, props]);

  const dialogPreset = useMemo((): RelativeFilterResponseBody | null => {
    if (dialogState.mode === 'create') {
      return null;
    }
    if (dialogState.mode === 'edit') {
      return presetController.presets.find((preset) => preset.id === dialogState.presetId) ?? null;
    }
    return null;
  }, [dialogState, presetController.presets]);

  const isDialogOpen = dialogState.mode !== 'closed';

  return (
    <>
      <HStack align="flex-end" spacing={1}>
        <Box flexGrow={1} minWidth={0}>
          <SavedFiltersDropdown
            presetController={presetController}
            label={i18n.t('entity.filterForm.savedFilters.label')}
            placeholder={i18n.t('entity.filterForm.savedFilters.placeholder')}
            onSelectPreset={handlePresetChange}
            onCreatePreset={handleCreateOption}
            data-testid={suffixTestId('savedFiltersDropdown', props)}
          />
        </Box>
        <IconButton
          icon="action/settings"
          priority="tertiary"
          isDisabled={isNil(presetController.displayedPresetId)}
          onClick={() => {
            if (isNotNil(presetController.displayedPresetId)) {
              setDialogState({mode: 'edit', presetId: presetController.displayedPresetId});
            }
          }}
          aria-label={i18n.t('entity.filterForm.savedFilters.editPreset')}
          data-testid={suffixTestId('editPresetButton', props)}
        />
      </HStack>

      <PresetEditorDialog
        key={dialogState.mode === 'edit' ? dialogState.presetId : dialogState.mode}
        isOpen={isDialogOpen}
        initialPreset={dialogPreset}
        onSave={handleSave}
        onDelete={dialogState.mode === 'edit' ? handleDeletePresetPress : undefined}
        onClose={() => setDialogState({mode: 'closed'})}
        data-testid={suffixTestId('presetEditor', props)}
      />
    </>
  );
}
